import { PageHeader, Tabs, Icon } from 'antd';
import React, { FC, useContext } from 'react';
import { AppContext } from '../../core/contexts/AppContext';

import config from '../../../config';
import { EmailSettingsCard } from '../components/EmailSettingsCard';
import { EditOrganization } from './EditOrganization';
import { SecuritySettingsCard } from './SecuritySettings';
import { SuperuserOrgSettingsContainer } from './SuperuserOrgSettings';
import { ConsumerSettingsCard } from '../components/ConsumerSettingsCard';
import { useQueryParam, StringParam } from 'use-query-params';
import { isBlockitAdmin } from '../../core/components/PrivateRoute';
import { ReferralSettingsCard } from '../components/ReferralSettingsCard';
import { AppointmentSettingsCard } from '../components/AppointmentSettingsCard';
import { FeatureFlag, hasFeature } from '../../../helpers';

export const OrgSettingsPage: FC<{}> = (): JSX.Element | null => {
  const { currentOrganization, switchMode } = useContext(AppContext);

  switchMode('admin');

  const [tab, setTab] = useQueryParam('tab', StringParam);
  const defaultTab = tab || 'profile';

  if (!currentOrganization) return null;

  return (
    <PageHeader title="Settings" className="main-content">
      <Tabs
        tabPosition="left"
        defaultActiveKey={defaultTab}
        onChange={setTab}
        onTabClick={(key: string) => {
          if (key === 'notifications') {
            window.open(`${config.endpoint}/organizations/${currentOrganization.id}/notifications`);
          } else if (key === 'bookingLog') {
            window.open(`${config.endpoint}/organizations/${currentOrganization.id}/integration/proposals`);
          } else if (key === 'actions') {
            window.open(`${config.endpoint}/organizations/${currentOrganization.id}`);
          } else if (key === 'import') {
            window.open(`${config.endpoint}/organizations/${currentOrganization.id}/profile_import`);
          } else if (key === 'taxonomies') {
            window.open(`${config.endpoint}/organizations/${currentOrganization.id}/taxonomies`);
          } else if (key === 'linkTypes') {
            window.open(`${config.endpoint}/organizations/${currentOrganization.id}/link_types`);
          } else if (key === 'schemas') {
            window.open(`${config.endpoint}/organizations/${currentOrganization.id}/schemas`);
          } else if (key === 'insurance') {
            window.open(`${config.endpoint}/organizations/${currentOrganization.id}/carriers`);
          } else if (key === 'bulkImageUpload') {
            window.open(`${config.endpoint}/organizations/${currentOrganization.id}/profile_image_upload`);
          } else if (key === 'profileRequiredFields') {
            window.open(`${config.endpoint}/organizations/${currentOrganization.id}/profile_required_fields`);
          } else if (key === 'locationRequiredFields') {
            window.open(`${config.endpoint}/organizations/${currentOrganization.id}/location_required_fields`);
          }
        }}
      >
        <Tabs.TabPane tab="Organization" key="organization">
          <EditOrganization />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Appointment Settings" key="appointments">
          <AppointmentSettingsCard organizationId={currentOrganization.id} />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Email Notifications" key="emailTemplates">
          <EmailSettingsCard organizationId={currentOrganization.id} />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Referral Settings" key="referral">
          <ReferralSettingsCard organizationId={currentOrganization.id} />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Consumer Settings" key="consumer">
          <ConsumerSettingsCard organizationId={currentOrganization.id} />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Security" key="security">
          <SecuritySettingsCard organizationId={currentOrganization.id} />
        </Tabs.TabPane>
        {isBlockitAdmin(currentOrganization) && <Tabs.TabPane tab="Notifications" key="notifications"></Tabs.TabPane>}
        <Tabs.TabPane tab="Appointment Booking Log" key="bookingLog"></Tabs.TabPane>
        <Tabs.TabPane tab="Actions" key="actions"></Tabs.TabPane>
        {currentOrganization?.locationRequiredFieldsEnabled ? (
          <Tabs.TabPane tab="Location Required Fields" key="locationRequiredFields"></Tabs.TabPane>
        ) : null}
        {currentOrganization?.profileRequiredFieldsEnabled ? (
          <Tabs.TabPane tab="Profile Required Fields" key="profileRequiredFields"></Tabs.TabPane>
        ) : null}
        {currentOrganization?.linkTypeAdministrationEnabled ? (
          <Tabs.TabPane tab="Link Type Management" key="linkTypes"></Tabs.TabPane>
        ) : null}
        {isBlockitAdmin(currentOrganization) && currentOrganization?.taxonomyAdministrationEnabled && (
          <Tabs.TabPane tab="Taxonomy Management" key="taxonomies"></Tabs.TabPane>
        )}
        {hasFeature(currentOrganization?.featureFlags, FeatureFlag.EnablePdmV2) &&
        currentOrganization?.organizationInsurancesEnabled ? (
          <Tabs.TabPane tab="Organization Insurance" key="insurance"></Tabs.TabPane>
        ) : null}
        {hasFeature(currentOrganization?.featureFlags, FeatureFlag.BulkImageUpload) ? (
          <Tabs.TabPane tab="Bulk Image Upload" key="bulkImageUpload"></Tabs.TabPane>
        ) : null}
        {hasFeature(currentOrganization?.featureFlags, FeatureFlag.CustomerDataImport) ? (
          <Tabs.TabPane tab="Profile Import" key="import"></Tabs.TabPane>
        ) : null}
        {/* eslint-disable-next-line max-len */}
        {isBlockitAdmin(currentOrganization) && hasFeature(currentOrganization?.featureFlags, FeatureFlag.EnablePdmV2) && (
          <Tabs.TabPane
            tab={
              <>
                <Icon type="lock" />
                Organization Schemas
              </>
            }
            key="schemas"
          ></Tabs.TabPane>
        )}
        {isBlockitAdmin(currentOrganization) && (
          <Tabs.TabPane
            tab={
              <>
                <Icon type="lock" />
                Feature Flags
              </>
            }
            key="account"
          >
            <SuperuserOrgSettingsContainer organizationId={currentOrganization.id} />
          </Tabs.TabPane>
        )}
      </Tabs>
    </PageHeader>
  );
};
